import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;

    this.#updateRemoveState(value);
  }

  /**
   * When the minus button doubles as a remove button (`data-remove-at-min`), swaps
   * its icon and accessible label between "decrease" and "remove" depending on
   * whether the next decrement would take the line item to zero.
   * @param {number} value - The current quantity value.
   */
  #updateRemoveState(value) {
    const { minusButton } = this.refs;
    if (!minusButton.hasAttribute('data-remove-at-min')) return;

    const isRemoveState = value <= 1;
    const minusIcon = minusButton.querySelector('.quantity-minus__icon-minus');
    const removeIcon = minusButton.querySelector('.quantity-minus__icon-remove');
    const label = minusButton.querySelector('.visually-hidden');

    minusIcon?.classList.toggle('hidden', isRemoveState);
    removeIcon?.classList.toggle('hidden', !isRemoveState);
    minusButton.classList.toggle('quantity-minus--remove', isRemoveState);

    if (label) {
      const { removeLabel, decreaseLabel } = minusButton.dataset;
      label.textContent = (isRemoveState ? removeLabel : decreaseLabel) ?? '';
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
